import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Plus, 
  BookOpen,
  Clock,
  FileText,
  X
} from 'lucide-react';
import { listEntries, createEntry } from '../lib/entries';
import type { Entry } from '../../../shared/lib/supabase';
import { toast } from 'sonner';

interface EntriesSidebarProps {
  projectId: string;
  onOpenEntry: (entryId: string, name: string) => void;
  onClose: () => void;
}

export function EntriesSidebar({ projectId, onOpenEntry, onClose }: EntriesSidebarProps) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newEntryName, setNewEntryName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadEntries();
  }, [projectId]);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const entriesList = await listEntries(projectId);
      setEntries(entriesList);
    } catch (error) {
      console.error('Error loading entries:', error);
      toast.error('Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntry = async () => {
    if (!newEntryName.trim()) {
      toast.error('Please enter an entry name');
      return;
    }

    try {
      setIsCreating(true);
      const entryId = await createEntry({
        name: newEntryName.trim(),
        project_id: projectId
      });

      toast.success('Entry created successfully!');
      setShowCreateDialog(false);
      setNewEntryName('');
      
      // Reload entries
      await loadEntries();
      
      // Open the new entry
      onOpenEntry(entryId, newEntryName.trim());
    } catch (error) {
      console.error('Error creating entry:', error);
      toast.error('Failed to create entry');
    } finally {
      setIsCreating(false);
    }
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredEntries = entries.filter(entry =>
    entry.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="w-64 bg-white flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-gray-600" />
            <h3 className="text-base font-normal text-gray-900">Entries</h3>
          </div>
          <button
            onClick={onClose}
            className="ml-1 p-0.5 rounded-sm hover:bg-gray-200 transition-all flex-shrink-0"
            title="Close entries sidebar"
          >
            <X className="h-4 w-4 text-gray-500 hover:text-gray-700" />
          </button>
        </div>
        
        {/* New Entry Button */}
        <button
          onClick={() => setShowCreateDialog(true)}
          className="w-full bg-gray-800 text-white px-3 py-1.5 rounded-sm hover:bg-gray-900 transition-colors flex items-center justify-center gap-2 text-sm mb-3 mt-4"
        >
          <Plus className="h-3 w-3" />
          New Entry
        </button>
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search entries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Separator */}
      <div className="border-b border-gray-200"></div>

      {/* Entries List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-8 px-4">
            <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              {searchQuery ? 'No entries found' : 'No entries yet'}
            </p>
          </div>
        ) : (
          <div className="py-1">
            {filteredEntries.map((entry) => (
              <button
                key={entry.id}
                onClick={() => onOpenEntry(entry.id, entry.name)}
                className="w-full px-4 py-2.5 hover:bg-gray-50 transition-colors text-left group border-b border-gray-100"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-3 w-3 text-gray-400 flex-shrink-0" />
                      <span className="text-sm font-normal text-gray-900 truncate">
                        {entry.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="h-3 w-3" />
                      <span>last edited {formatRelativeTime(entry.updated_at)}</span>
                      {entry.word_count > 0 && (
                        <>
                          <span className="mx-1">•</span>
                          <span>{entry.word_count} words</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Create Entry Dialog */}
      {showCreateDialog && (
        <div className="absolute inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-96 max-w-full mx-4">
            <div className="px-5 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-normal text-gray-900">New Entry</h3>
                <button
                  onClick={() => {
                    setShowCreateDialog(false);
                    setNewEntryName('');
                  }}
                  className="p-1 hover:bg-gray-100 rounded-sm transition-colors"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>
            
            <div className="px-5 py-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-normal text-gray-700 mb-1">
                    Entry Name *
                  </label>
                  <input
                    type="text"
                    value={newEntryName}
                    onChange={(e) => setNewEntryName(e.target.value)}
                    placeholder="Lab Notebook Entry"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isCreating) {
                        handleCreateEntry();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            
            <div className="px-5 py-3 border-t border-gray-200 flex gap-2">
              <button
                onClick={() => {
                  setShowCreateDialog(false);
                  setNewEntryName('');
                }}
                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-sm text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateEntry}
                disabled={!newEntryName.trim() || isCreating}
                className="flex-1 px-3 py-1.5 text-sm bg-gray-800 text-white rounded-sm hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}